import React, { useEffect, useState } from 'react';
import Loader from "../components/Loader"; 
import Paginator from "../components/Paginator"; 
import ApiService from "../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {formatDate} from "../utils/helper";
import {exportCSV} from "../utils/helper";
import {statusColor} from "../assets/utils/statusColors";
import { selectStoredPermissions } from '../stores/permissionsSlice';



const Transactions = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({});
    const [currency] = useState(process.env.REACT_APP_CURRENCY);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Initialize state from URL params
    const [apiData, setApiData] = useState({
        search: searchParams.get('search') || "",
        status: searchParams.get('status') || "",
        payment_method: searchParams.get('payment_method') || "",
        payment_gateway: searchParams.get('payment_gateway') || "",
        is_export: searchParams.get('is_export') || false,
        daterange: searchParams.get('daterange') ? 
            JSON.parse(searchParams.get('daterange')) : [],
        page: parseInt(searchParams.get('page')) || 1,
    });

    // Update URL when filters change
    useEffect(() => {
        const params = new URLSearchParams();
        if (apiData.search) params.set('search', apiData.search);
        if (apiData.status) params.set('status', apiData.status);
        if (apiData.payment_method) params.set('payment_method', apiData.payment_method);
        if (apiData.payment_gateway) params.set('payment_gateway', apiData.payment_gateway);
        if (apiData.is_export) params.set('is_export', apiData.is_export);
        if (apiData.daterange) params.set('daterange', JSON.stringify(apiData.daterange));
        if (apiData.page > 1) params.set('page', apiData.page.toString());
        
        navigate(`?${params.toString()}`, { replace: true });
    }, [apiData.status, apiData.search, apiData.payment_method, apiData.payment_gateway,
         apiData.daterange, apiData.page, apiData.is_export, navigate]);

    useEffect(() => {
        getTransactions();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.payment_method,
        apiData.payment_gateway, apiData.status, apiData.is_export]); 

    const getTransactions = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "/transactions",
                data: apiData
            });
            const data = response.data;
            if(apiData.is_export){
                exportData(data.data.transactions, 'transactions_data');
                setApiData({...apiData, is_export:false});
                setIsLoader(false);
                return 0;
            }
            if (data.status) {
                setTransactions(data.data.transactions);
                setPagination(data.data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
            console.error('Error fetching transactions:', error);
            toast.error('Failed to fetch transactions');
        }
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            daterange: [],
            page: 1,
            status: '',
            payment_method: '',
            payment_gateway: '',
        });
    };

    const exportData = (data, headersMap = null) => {
        const columnsMap = {
            transaction_id: 'Transaction ID',
            user: 'Customer',
            order: 'Order Code',
            amount: 'Amount',
            currency: 'Currency',
            status: 'Status',
            createdAt: 'Date',
            description: 'Description',
            failure_reason: 'Failure Reason'
        };

        const processedData = data.map(item => {
            const newItem = {};
            Object.keys(columnsMap).forEach(key => {
                // Format specific fields if needed
                let value = item[key];
                
                if (key === 'user' && value) {
                    value = value.name;
                }
                if (key === 'order' && value) {
                    value = value.order_code;
                }
                if (key === 'amount' && value) {
                    value = (value / 100).toFixed(2); // Convert from cents
                }
                
                newItem[key] = value !== undefined ? value : '';
            });
            return newItem;
        });

        exportCSV(processedData, columnsMap, 'transactions_data');
    };

    const updateTransactionStatus = async (transactionId, newStatus) => {
        try {
            const response = await ApiService.request({
                method: 'PUT',
                url: `admin/transactions/${transactionId}/status`,
                data: { status: newStatus }
            });

            if (response.data.status) {
                toast.success('Transaction status updated successfully');
                getTransactions();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error updating transaction status:', error);
            toast.error('Failed to update transaction status');
        }
    };

    const processRefund = async (transactionId) => {
        const { value: refundAmount } = await Swal.fire({
            title: 'Process Refund',
            input: 'number',
            inputLabel: 'Refund Amount',
            inputPlaceholder: 'Enter refund amount',
            showCancelButton: true,
            confirmButtonText: 'Process Refund',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (!value) {
                    return 'Please enter a refund amount';
                }
                if (isNaN(value) || parseFloat(value) <= 0) {
                    return 'Please enter a valid amount';
                }
            }
        });

        if (refundAmount) {
            const { value: reason } = await Swal.fire({
                title: 'Refund Reason',
                input: 'text',
                inputLabel: 'Reason for refund',
                inputPlaceholder: 'Enter refund reason',
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                inputValidator: (value) => {
                    if (!value) {
                        return 'Please enter a reason for refund';
                    }
                }
            });

            if (reason) {
                try {
                    const response = await ApiService.request({
                        method: 'POST',
                        url: `admin/transactions/${transactionId}/refund`,
                        data: { 
                            refund_amount: parseFloat(refundAmount),
                            reason: reason
                        }
                    });

                    if (response.data.status) {
                        toast.success('Refund processed successfully');
                        getTransactions();
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    console.error('Error processing refund:', error);
                    toast.error('Failed to process refund');
                }
            }
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0 text-capitalize">
                            Transactions</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Transactions</li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        {/* Export Button */}
                        <button type="button" onClick={()=> setApiData({...apiData, is_export:true})}  className='btn btn-secondary btn-sm'> 
                            <i className="bi bi-upload"></i> Export
                        </button>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-3 mb-3">
                                            <div className="d-flex align-items-center position-relative mb-1">
                                                <input type="text" value={apiData.search} onChange={e => setApiData({ ...apiData, search: e.target.value })} className="form-control form-control-solid me-5"
                                                 placeholder="Search Transaction ID.."/>
                                            </div>
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <DatePicker
                                                selectsRange={true}
                                                startDate={apiData.daterange[0]}
                                                endDate={apiData.daterange[1]}
                                                onChange={e => setApiData({ ...apiData, daterange: e })} 
                                                isClearable={true}
                                                maxDate={new Date()}
                                                className='form-control form-control-solid'
                                                placeholderText={('Select Daterange')}
                                            />
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <select value={apiData.status} className='form-select' 
                                                onChange={e => setApiData({ ...apiData, status: e.target.value })}>
                                                <option value="">Select Status</option>
                                                <option value="completed">Completed</option>
                                                <option value="pending">Pending</option>
                                                <option value="failed">Failed</option>
                                                <option value="cancelled">Cancelled</option>
                                                <option value="refunded">Refunded</option>
                                                <option value="processing">Processing</option>
                                            </select>
                                        </div>
                                        
                                        <div className="col-md-3 mt-2">
                                            <button type="button" onClick={clearFilter} className={`btn btn-sm btn-secondary mx-1 ${!apiData.search && !apiData.daterange ? 'd-none' : ''}`}>
                                                Clear
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_transactions_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Transaction ID</th>
                                                <th>Customer</th>
                                                <th>Order</th>
                                                <th>Amount</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {transactions.length > 0 ? transactions.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <span className='text-gray-800 fw-bold'>
                                                            {item.transaction_id}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {item.user && 
                                                            <div>
                                                                {item.user.name} <br />
                                                                <small className="text-muted">{item.user.email}</small>
                                                            </div>
                                                        }
                                                    </td>
                                                    <td>
                                                        {item.order?.order_code ? (
                                                            <Link to={`/orders/${item.order._id}`} className='text-primary'>
                                                                #{item.order.order_code}
                                                            </Link>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong>{currency} {(item.amount).toFixed(2)}</strong>
                                                        </div>
                                                    </td>
                                                    
                                                    <td>
                                                        <button className="p-0 btn btn-transparent">
                                                            <span className={`badge bg-${statusColor[item.status] || 'danger'}`}>
                                                                {item.status}
                                                            </span>
                                                        </button>
                                                    </td>
                                                   
                                                    <td>
                                                        {formatDate(item.createdAt,'DD MMM YYYY, hh:mm A')}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="9" className="text-dark text-center mt-2">No data found!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table> 
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center"  />
                                    )}
                                    <span>Total Records: {pagination?.total_records}</span>
                                    
                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Transactions;